/**
 * Обгортка браузерної бібліотеки підпису ІІТ (euscp) для КЕП.
 *
 * Бібліотека вендорена у /public/eusign/ (euutils → euscpt → euscpm → euscp,
 * ~7.6МБ) і вантажиться ЛІНИВО (динамічні <script>) лише коли користувач
 * відкриває панель підпису. Крипто-ресурси (CAs.json, CACertificates.p7b,
 * сертифікати ДПС) роздає backend: /api/v1/eusign/assets/*.
 *
 * Ключ НЕ покидає браузер: читання/підпис/шифрування — локально (WASM/asm.js);
 * назовні йдуть лише запити CMP/TSP/OCSP через наш проксі /api/v1/eusign/proxy
 * (формат ІІТ ProxyHandler) та вже зашифровані конверти.
 *
 * Джерело патернів — офіційне демо ІІТ js.sign.eu.iit.com.ua (euscptest.js).
 */

// Мінімальні типи для API euscp (бібліотека не має TS-типів).
export interface EuSignCA {
  issuerCNs: string[];
  address: string;
  ocspAccessPointAddress: string;
  ocspAccessPointPort: string;
  cmpAddress: string;
  tspAddress: string;
  tspAddressPort: string;
  certsInKey?: boolean;
}

export interface KeyOwnerInfo {
  subjCN: string;
  subjOrg?: string;
  subjTitle?: string;
  issuerCN: string;
  serial: string;
  subjDRFOCode?: string;
  subjEDRPOUCode?: string;
}

declare global {
  interface Window {
    EUSignCP?: () => any;
    EUSignCPModuleInitialized?: (ok: boolean) => void;
  }
}

const PROXY_URL = '/api/v1/eusign/proxy';
const ASSETS = '/api/v1/eusign/assets';
const SCRIPTS = ['euutils.js', 'euscpt.js', 'euscpm.js', 'euscp.js'];

let euSign: any = null;
let modulePromise: Promise<void> | null = null;
let casCache: EuSignCA[] | null = null;

function injectScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Не вдалося завантажити ${src}`));
    document.head.appendChild(s);
  });
}

/** Ліниве завантаження бібліотеки + ініціалізація крипто-модуля. */
export function loadEuSign(): Promise<void> {
  if (modulePromise) return modulePromise;
  modulePromise = (async () => {
    const ready = new Promise<void>((resolve, reject) => {
      window.EUSignCPModuleInitialized = (ok: boolean) => {
        if (ok) resolve();
        else reject(new Error('Криптографічну бібліотеку не ініціалізовано'));
      };
    });
    for (const f of SCRIPTS) {
      // послідовно: euscpm потребує euscpt; euscp сигналить через callback
      await injectScript(`/eusign/${f}`);
    }
    await ready;
    euSign = window.EUSignCP!();
    euSign.Initialize();
    euSign.SetJavaStringCompliant(true);
    euSign.SetCharset('UTF-8');

    // Налаштування за замовчуванням (патерн демо ІІТ setDefaultSettings)
    euSign.SetXMLHTTPProxyService(PROXY_URL);
    let st = euSign.CreateFileStoreSettings();
    st.SetPath('/certificates');
    st.SetSaveLoadedCerts(true);
    euSign.SetFileStoreSettings(st);
    euSign.SetProxySettings(euSign.CreateProxySettings());
    euSign.SetCMPSettings(euSign.CreateCMPSettings());
    euSign.SetLDAPSettings(euSign.CreateLDAPSettings());

    // НЕ вмикаємо глобальний offline (він ріже і CMP — читання ключа впаде
    // «Доступ до сервера ЦСК неможливий»). Натомість точково ВИМИКАЄМО лише
    // online-перевірку статусу OCSP і TSP-мітку часу: джерело помилок
    // «не вдалося отримати статус за OCSP» — недоступність responder'а АЦСК.
    // Статус сертифіката перевіряє САМ ДПС на приймальному шлюзі; CMP лишається
    // online (потрібен для читання ключа / витягу сертифікатів).
    const mode = euSign.CreateModeSettings();
    mode.SetOfflineMode(false);
    euSign.SetModeSettings(mode);
    const tsp = euSign.CreateTSPSettings();
    tsp.SetGetStamps(false);
    euSign.SetTSPSettings(tsp);
    const ocsp = euSign.CreateOCSPSettings();
    ocsp.SetUseOCSP(false);
    euSign.SetOCSPSettings(ocsp);

    // Сертифікати всіх АЦСК — у файлове сховище бібліотеки (для побудови ланцюга)
    const p7b = await fetchBytes(`${ASSETS}/CACertificates.p7b`);
    euSign.SaveCertificates(p7b);
  })();
  modulePromise.catch(() => { modulePromise = null; });
  return modulePromise;
}

async function fetchBytes(url: string): Promise<Uint8Array> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`HTTP ${r.status} при завантаженні ${url}`);
  return new Uint8Array(await r.arrayBuffer());
}

/** Перелік АЦСК (для селекта на панелі). */
export async function getCAs(): Promise<EuSignCA[]> {
  if (casCache) return casCache;
  const r = await fetch(`${ASSETS}/CAs.json`);
  if (!r.ok) throw new Error('Не вдалося завантажити перелік АЦСК');
  casCache = await r.json();
  return casCache!;
}

/**
 * Виставити CMP під обраний АЦСК. OCSP/TSP лишаються ВИМКНЕНІ (offline —
 * див. loadEuSign): статус сертифіката перевіряє ДПС на приймальному шлюзі.
 * CMP лишаємо online — ним дістаємо сертифікати з ключа, якщо їх там немає.
 */
function applyCASettings(ca: EuSignCA | null) {
  const useCMP = !!ca && !!ca.address && !!ca.cmpAddress;
  const cmp = euSign.CreateCMPSettings();
  cmp.SetUseCMP(useCMP);
  if (useCMP) {
    cmp.SetAddress(ca!.cmpAddress);
    cmp.SetPort('80');
  }
  euSign.SetCMPSettings(cmp);
}

/**
 * Зчитати особистий ключ (Key-6.dat / *.pfx / *.zs2 ...) з АВТОвизначенням АЦСК.
 *
 * ca — необов'язковий (можна null): АЦСК визначається сам. Спроби по черзі:
 *  1) сертифікати вшиті у ключ (нові ключі Дії/хмарні) — читаємо напряму;
 *  2) якщо ні — тягнемо сертифікати по CMP: спершу обраний АЦСК (якщо задано),
 *     інакше перебираємо УСІ CMP-адреси з CAs.json одним запитом
 *     (GetCertificatesByKeyInfo сам обере правильний видавець за ключем).
 *
 * Так користувачу НЕ треба вручну обирати видавця — просто ключ + пароль.
 */
/** Зчитати ключ у СЛОТ бібліотеки з CMP-fallback (сертифікати не у
 *  контейнері — типово ключі ПриватБанку: дістаємо по CMP з АЦСК).
 *  Використовується і при першому читанні, і при ЛІНИВОМУ відновленні
 *  після перезавантаження сторінки (activateKey) — раніше fallback був
 *  лише у першому читанні, тому після F5 такі ключі «не авторизовані»
 *  (скарга власника 2026-08-17). */
async function readIntoSlot(
  keyBytes: Uint8Array, password: string, ca: EuSignCA | null,
): Promise<void> {
  await loadEuSign();
  applyCASettings(ca);
  resetSlot();  // лише слот бібліотеки — реєстр раніше зчитаних ключів НЕ чіпаємо
  try {
    // Крок 1: сертифікати у ключі
    euSign.ReadPrivateKeyBinary(keyBytes, password);
  } catch (e: any) {
    // Крок 2: дістати сертифікати по CMP (авто-перебір АЦСК)
    try {
      const keyInfo = euSign.GetKeyInfoBinary(keyBytes, password);
      const certs = await fetchCertsViaCmp(keyInfo, ca);
      euSign.SaveCertificates(certs);
      euSign.ReadPrivateKeyBinary(keyBytes, password);
    } catch (e2) {
      // Перебір вичерпано → просимо обрати АЦСК вручну (панель ловить).
      if (e2 instanceof CaAutoDetectError) throw e2;
      // Iнакше — ПЕРВИННА помилка читання (зрозуміліша: пароль тощо).
      throw normalizeErr(e);
    }
  }
}

export async function readPrivateKey(
  keyBytes: Uint8Array, password: string, ca: EuSignCA | null,
  fileName = '',
): Promise<KeyOwnerInfo> {
  await readIntoSlot(keyBytes, password, ca);
  const info = euSign.GetPrivateKeyOwnerInfo();
  const owner: KeyOwnerInfo = {
    subjCN: info.GetSubjCN(),
    subjOrg: safeCall(() => info.GetSubjOrg()),
    subjTitle: safeCall(() => info.GetSubjTitle()),
    issuerCN: info.GetIssuerCN(),
    serial: info.GetSerial(),
    subjDRFOCode: safeCall(() => info.GetSubjDRFOCode()),
    subjEDRPOUCode: safeCall(() => info.GetSubjEDRPOUCode()),
  };
  // Запам'ятати у реєстр (файл+пароль+власник) — щоб перемикатись між кількома
  // ключами без повторного вводу пароля. Дублікат оновлюється.
  registerKey(owner, fileName, keyBytes, password, ca);
  return owner;
}

/** Список CMP-серверів для авто-визначення АЦСК: обраний → або всі з
 *  CAs.json, АЛЕ пріоритезовані (скарга власника 2026-08-18: сторінка
 *  «жорстко висла на хвилину» — euscp СИНХРОННО перебирав 30 CMP-серверів
 *  у головному потоці). Першими йдуть АЦСК ВЖЕ ЗЧИТАНИХ ключів сесії
 *  (другий ключ орг — печатка Медоїд — майже завжди того ж АЦСК, що
 *  підпис) + останній успішний CMP з localStorage. */
const _CMP_LS_KEY = 'eusign.lastCmp.v1';

// Холодний старт (перший ключ сесії, без підказок): найімовірніші АЦСК
// першими. Ключі БЕЗ сертифікатів у контейнері — це насамперед
// ПриватБанк (Key-6.dat), далі ІДД ДПС, «Україна», MASTERKEY, Дія.
const _POPULAR_CMP = [
  'acsk.privatbank.ua', 'ca.informjust.ua', 'acskidd.gov.ua',
  'uakey.com.ua', 'masterkey.ua', 'ca.diia.gov.ua',
];

/** Прогрес авто-перебору АЦСК — панель показує «перевіряємо N/M». */
export type CmpProgress = { done: number; total: number; host: string };
let _cmpProgressCb: ((p: CmpProgress) => void) | null = null;
export function onCmpProgress(cb: ((p: CmpProgress) => void) | null): void {
  _cmpProgressCb = cb;
}

/** Авто-перебір не знайшов видавця — панель просить обрати АЦСК вручну. */
export class CaAutoDetectError extends Error {
  constructor() {
    super('Не вдалося автоматично визначити ваш АЦСК (центр сертифікації). '
      + 'Оберіть видавця ключа зі списку і повторіть.');
    this.name = 'CaAutoDetectError';
  }
}

/**
 * Дістати сертифікати ключа по CMP — ПО ОДНОМУ серверу за виклик.
 *
 * Раніше весь список (30+) ішов ОДНИМ викликом GetCertificatesByKeyInfo —
 * euscp перебирав їх синхронно у головному потоці, сторінка «мертво»
 * висіла до хвилини (скарга власника 2026-08-18 і повторно 19-го).
 * Тепер: між спробами віддаємо event loop (спінер живий, видно прогрес),
 * пам'ятаємо ТОЧНИЙ сервер-переможець, а після 12 невдач зупиняємось і
 * просимо юзера обрати АЦСК вручну (селект на панелі вже є) — замість
 * нескінченного мовчазного перебору.
 */
async function fetchCertsViaCmp(
  keyInfo: Uint8Array, ca: EuSignCA | null,
): Promise<Uint8Array> {
  const servers = await cmpServerList(ca);
  const cap = ca?.cmpAddress ? servers.length : Math.min(servers.length, 12);
  for (let i = 0; i < cap; i++) {
    const server = servers[i]!;
    // Віддати кадр UI перед СИНХРОННИМ (1-3 с) CMP-запитом бібліотеки.
    await new Promise((r) => setTimeout(r, 0));
    const host = server.replace(/:80$/, '');
    _cmpProgressCb?.({ done: i + 1, total: cap, host });
    try {
      const certs: Uint8Array =
        euSign.GetCertificatesByKeyInfo(keyInfo, [server]);
      try { localStorage.setItem(_CMP_LS_KEY, host); } catch { /* ok */ }
      console.info(`CMP: сертифікати знайдено на ${host} (спроба ${i + 1})`);
      return certs;
    } catch { /* наступний сервер */ }
  }
  throw new CaAutoDetectError();
}

async function cmpServerList(ca: EuSignCA | null): Promise<string[]> {
  if (ca?.cmpAddress) return [`${ca.cmpAddress}:80`];
  const cas = await getCAs();
  const known = _keys
    .map((k) => (k.owner.issuerCN || '').toLowerCase())
    .filter(Boolean);
  let lastOk = '';
  try { lastOk = localStorage.getItem(_CMP_LS_KEY) || ''; } catch { /* ok */ }
  const score = (c: EuSignCA): number => {
    if (!c.cmpAddress) return 9;
    if (lastOk && c.cmpAddress === lastOk) return 0;
    const names = (c.issuerCNs || []).map((n) => n.toLowerCase());
    if (known.some((ki) => names.some(
      (n) => n.includes(ki) || ki.includes(n)))) return 1;
    if (_POPULAR_CMP.some((h) => (c.cmpAddress || '').includes(h))) return 2;
    return 3;
  };
  return cas
    .filter((c) => c.cmpAddress)
    .sort((a, b) => score(a) - score(b))
    .map((c) => `${c.cmpAddress}:80`);
}

// Власник АКТИВНОГО ключа (зараз у слоті бібліотеки) — тримається у пам'яті
// вкладки на СЕСІЮ (ключ НЕ покидає браузер, у storage не пишеться).
let _sessionOwner: KeyOwnerInfo | null = null;

// ── Реєстр КІЛЬКОХ зчитаних ключів (для бухгалтерів на кілька юросіб) ──────
// Бібліотека ІІТ тримає лише ОДИН активний ключ у слоті. Тож ми зберігаємо
// файл+пароль+власника кожного зчитаного ключа у пам'яті вкладки, а при виборі
// «підписати цим ключем» — перечитуємо потрібний у слот (activateKey). Пароль
// живе у пам'яті вкладки (як і сам ключ) — не пишеться на диск/у storage.
export interface StoredKey {
  id: string;            // стабільний ідентифікатор запису
  owner: KeyOwnerInfo;   // ПІБ/код/АЦСК — для показу і звірки
  fileName: string;
  keyBytes: Uint8Array;  // вміст файлу ключа (пам'ять вкладки)
  password: string;      // пароль (пам'ять вкладки)
  ca: EuSignCA | null;   // обраний АЦСК (або null=авто) — для перечитування
  readAt: number;        // час зчитування (TTL сесії ключа)
}

// TTL активного ключа (зауваження власника 2026-08-16: бейджі лишались
// зеленими вічно — sessionStorage переживає відновлення вкладки).
// 8 годин = робочий день; далі ключ прибирається з сесії, потрібна
// повторна активація паролем (збережені на сервері — сірий бейдж).
const KEY_TTL_MS = 8 * 60 * 60 * 1000;

function _purgeExpiredKeys(): void {
  const now = Date.now();
  const expired = _keys.filter((k) => now - (k.readAt || 0) > KEY_TTL_MS);
  if (expired.length === 0) return;
  _keys = _keys.filter((k) => now - (k.readAt || 0) <= KEY_TTL_MS);
  if (expired.some((k) => k.id === _activeKeyId)) {
    resetSlot();
    _sessionOwner = null;
    const last = _keys[_keys.length - 1];
    _activeKeyId = last ? last.id : null;
  }
  _persistKeys();
  setTimeout(() => _notifyKeysChanged(), 0);
}

let _keys: StoredKey[] = [];
let _activeKeyId: string | null = null;
let _keyIdSeq = 0;

// ── Персист реєстру у sessionStorage (2026-08-07, рішення власника) ────────
// F5/повне перезавантаження скидали памʼять вкладки → бухгалтер перечитував
// ключ на кожній сторінці. sessionStorage живе ДО ЗАКРИТТЯ ВКЛАДКИ і не
// покидає браузер (на диск довше сесії не пишеться, між вкладками не
// шариться) — свідомий компроміс: зручність сесії при тому ж периметрі
// (XSS і так бачить памʼять вкладки). Слот бібліотеки відновлюється
// ЛІНИВО — activateKey перечитує ключ при першій крипто-операції.
const _SS_KEY = 'eusign.keys.v1';

function _persistKeys(): void {
  try {
    sessionStorage.setItem(_SS_KEY, JSON.stringify({
      seq: _keyIdSeq,
      active: _activeKeyId,
      keys: _keys.map((k) => ({
        id: k.id, owner: k.owner, fileName: k.fileName,
        keyB64: bytesToBase64(k.keyBytes), password: k.password, ca: k.ca,
        readAt: k.readAt,
      })),
    }));
  } catch { /* квота/приватний режим — працюємо без персисту */ }
}

function _restoreKeys(): void {
  try {
    const raw = sessionStorage.getItem(_SS_KEY);
    if (!raw) return;
    const data = JSON.parse(raw);
    _keys = (data.keys ?? []).map((k: any) => ({
      id: k.id, owner: k.owner, fileName: k.fileName,
      keyBytes: base64ToBytes(k.keyB64), password: k.password,
      ca: k.ca ?? null,
      // Старі записи без readAt → 0 → одразу протухають (переавторизація).
      readAt: k.readAt ?? 0,
    }));
    _activeKeyId = data.active ?? null;
    _keyIdSeq = data.seq ?? _keys.length;
  } catch { /* бите значення — ігноруємо */ }
}

_restoreKeys();

/** Код особи ключа: ЄДРПОУ (юрособа) АБО РНОКПП/ІПН (ФОП/фізособа). */
export function keyCode(owner: KeyOwnerInfo | null): string {
  if (!owner) return '';
  return (owner.subjEDRPOUCode || owner.subjDRFOCode || '').trim();
}

/**
 * Електронна ПЕЧАТКА (2026-08-14): сертифікат печатки виданий на САМУ
 * організацію — має ЄДРПОУ, але БЕЗ РНОКПП особи (у підпису директора
 * є і те, і те). Той самий критерій, що у квитанціях «Вчасно».
 */
export function isSealOwner(owner: KeyOwnerInfo | null): boolean {
  if (!owner) return false;
  return !!owner.subjEDRPOUCode && !(owner.subjDRFOCode || '').trim();
}

/** Усі зчитані у сесії ключі (для списку/вибору у панелі).
 *  Протухлі за TTL — прибираються (зауваження власника 2026-08-16). */
export function listKeys(): StoredKey[] {
  _purgeExpiredKeys();
  return _keys;
}

/** id активного ключа (того, що зараз у слоті бібліотеки). */
export function activeKeyId(): string | null {
  _purgeExpiredKeys();
  return _activeKeyId;
}

/** Додати ключ у реєстр (після успішного зчитування). Дублікат за кодом+серійником
 *  оновлюється, а не плодиться. Повертає доданий/оновлений запис. */
function registerKey(
  owner: KeyOwnerInfo, fileName: string, keyBytes: Uint8Array,
  password: string, ca: EuSignCA | null,
): StoredKey {
  const dup = _keys.find(
    (k) => keyCode(k.owner) === keyCode(owner) && k.owner.serial === owner.serial);
  const rec: StoredKey = {
    id: dup ? dup.id : `k${++_keyIdSeq}`,
    owner, fileName, keyBytes, password, ca,
    readAt: Date.now(),
  };
  const dupIdx = dup ? _keys.indexOf(dup) : -1;
  if (dupIdx >= 0) _keys[dupIdx] = rec;
  else _keys.push(rec);
  _activeKeyId = rec.id;  // щойно зчитаний — активний
  _persistKeys();
  return rec;
}

/**
 * Зробити ключ активним (перечитати у слот бібліотеки). Потрібно перед
 * будь-якою крипто-операцією, коли зчитано КІЛЬКА ключів. Якщо ключ уже
 * активний — no-op. Повертає власника активного ключа.
 */
export async function activateKey(id: string): Promise<KeyOwnerInfo> {
  _purgeExpiredKeys();
  const rec = _keys.find((k) => k.id === id);
  if (!rec) throw new Error('Ключ не знайдено у списку зчитаних');
  // Активне користування ключем продовжує TTL сесії ключа.
  rec.readAt = Date.now();
  _persistKeys();
  if (_activeKeyId === id && isKeyReaded()) return rec.owner;
  try {
    await readIntoSlot(rec.keyBytes, rec.password, rec.ca);
  } catch (e: any) {
    // Невідновний ключ (вимога власника 2026-08-17): прибрати з рядка —
    // зелений бейдж не має брехати про авторизацію.
    removeKey(id);
    _notifyKeysChanged();
    throw new Error(
      `Сесію ключа «${rec.owner.subjCN}» не вдалося відновити `
      + `(${e?.message ?? e}). Ключ прибрано зі списку — зчитайте його заново.`);
  }
  _sessionOwner = rec.owner;
  _activeKeyId = id;
  return rec.owner;
}

/** Однакові коди ЄДРПОУ/РНОКПП (ведучі нулі не рахуються). */
export function sameKeyCode(a: string, b: string): boolean {
  const na = (a || '').trim().replace(/^0+/, '');
  const nb = (b || '').trim().replace(/^0+/, '');
  return na !== '' && na === nb;
}

/**
 * ЄДИНИЙ підбір ключа для підпису від імені юрособи (уніфікація власника
 * 2026-08-17: Кабінет ДПС і Журнал ЕДО — одна логіка):
 * - 0 ключів → «зчитайте ключ»;
 * - є код юрособи → ключ з тим самим кодом (звірка ДО підпису);
 * - код невідомий → активний/перший;
 * - жоден не підходить → помилка з переліком зчитаних кодів.
 * Активує обраний ключ (ліниве відновлення слота після перезавантаження —
 * невідновний ключ прибирається зі списку всередині activateKey).
 */
export async function pickKeyForCode(
  wantCode: string, orgLabel: string,
): Promise<KeyOwnerInfo> {
  const list = listKeys();
  if (!list.length) throw new Error('Спершу зчитайте ключ КЕП (угорі).');
  // ПЕЧАТКУ для підписання документа НЕ обираємо автоматично (факт
  // 2026-08-18, Медоїд: два ключі одного ЄДРПОУ — печатка і підпис;
  // find() брав перший-ліпший → підписали ПЕЧАТКОЮ → ДПС ERROR_DECRYPT
  // без квитанції). Звіт підписує ПОСАДОВА ОСОБА; печатка — лише там,
  // де накладається другою свідомо (own-sign ЕДО).
  const preferSign = (ks: StoredKey[]): StoredKey | undefined =>
    ks.find((k) => !isSealOwner(k.owner)) ?? ks[0];
  const want = (wantCode || '').trim();
  if (!want) {
    const cur = _activeKeyId && list.find((k) => k.id === _activeKeyId);
    const pick = (cur && !isSealOwner(cur.owner) ? cur : preferSign(list))!;
    return activateKey(pick.id);
  }
  const matches = list.filter((k) => sameKeyCode(keyCode(k.owner), want));
  const match = preferSign(matches);
  if (match) return activateKey(match.id);
  const have = list.map((k) => keyCode(k.owner)).filter(Boolean).join(', ');
  throw new Error(
    `Немає зчитаного ключа для «${orgLabel}» (код ${want}). `
    + `Зчитано ключі: ${have || '—'}. Додайте ключ саме цієї юрособи `
    + `(кнопка «+ додати ще ключ КЕП»).`);
}

// Подія для панелей «Ключі КЕП»: склад реєстру змінився поза діями юзера
// (протухання TTL, невідновний ключ) — бейджі мають оновитись одразу.
function _notifyKeysChanged(): void {
  try {
    window.dispatchEvent(new CustomEvent('eusign-keys-changed'));
  } catch { /* SSR/тести */ }
}

/** Прибрати ОДИН ключ з реєстру (кнопка × у списку). Якщо він був активний —
 *  скидаємо слот. */
export function removeKey(id: string): void {
  _keys = _keys.filter((k) => k.id !== id);
  if (_activeKeyId === id) {
    resetSlot();
    const last = _keys[_keys.length - 1];
    _activeKeyId = last ? last.id : null;
    _sessionOwner = null;
  }
  _persistKeys();
}

/** Скинути лише слот бібліотеки (без чистки реєстру). */
function resetSlot(): void {
  if (euSign?.IsPrivateKeyReaded?.()) {
    try { euSign.ResetPrivateKey(); } catch { /* no-op */ }
  }
}

export function resetKey(): void {
  resetSlot();
  _sessionOwner = null;
  _keys = [];
  _activeKeyId = null;
  try { sessionStorage.removeItem(_SS_KEY); } catch { /* no-op */ }
}

export function isKeyReaded(): boolean {
  return !!euSign?.IsPrivateKeyReaded?.();
}

/** Власник АКТИВНОГО ключа (у слоті). null якщо жоден ключ не зчитано. */
export function sessionKeyOwner(): KeyOwnerInfo | null {
  return isKeyReaded() ? _sessionOwner : null;
}

/**
 * Крипто-ланцюг подання у ДПС — ЗВІРЕНО З КОДОМ Е-КАБІНЕТУ
 * (cabinet.tax.gov.ua chunk-HKSLCC77: signData + addSignHeaderDp):
 *
 *  1. підпис CAdES-T (SetRuntimeParameter("SignType", 4) — З TSP-міткою);
 *  2. СЕКЦІЯ підпису вручну: 'UA1_SIGN'\\0 + 4 байти довжини (LE) + p7s
 *     (саме так робить Е-кабінет у addSignHeaderDp);
 *  3. шифрування секції на сертифікат ДПС (EnvelopDataEx, signData=false);
 *  4. AppendTransportHeader('UA1', ...) — TRANSPORTABLE-заголовок наказу №499.
 */
const EU_SIGN_TYPE_PARAMETER = 'SignType';
// Наказ МФУ №557 (порядок обміну е-документами з контролюючими органами)
// вимагає підпис з ПОВНИМИ даними перевірки — CAdES-X Long (усередині
// OCSP-відповіді про статус сертифіката). M.E.Doc підписує саме так.
// Прецедент 2026-07-09: наш CAdES-T шлюз ПРИЙМАВ (кв.№1 з розпізнаним
// підписантом), але центральна система не завершувала прийом — «no data in
// Sent» у веб-кабінеті і жодної кв.№2 понад добу. X-Long вимагає робочий
// OCSP видавця ключа (йде через наш проксі, як TSP/CMP); якщо OCSP
// недоступний — fallback на CAdES-T (стара поведінка, гірше не стає).
const EU_SIGN_TYPE_CADES_T = 4;
const EU_SIGN_TYPE_CADES_X_LONG = 16;
const EU_SIGN_TYPE_CADES_BES = 1;

/**
 * Заголовок Authorization для API приватної частини ЕК ДПС
 * (/ws/public_api/post/incoming, .../ta/debt тощо).
 *
 * Формат (докум. cabinet.tax.gov.ua/help/api-registers-int.html): рядок
 * ЄДРПОУ/РНОКПП, підписаний ВНУТРІШНІМ підписом ключа з доданим сертифікатом,
 * BASE64. Це звичайний CAdES-BES (без TSP/OCSP) — тож примусово ставимо
 * SignType=BES (міг лишитись CAdES-T/X-Long після підпису звіту).
 */
export function signApiAuthHeader(edrpou: string): string {
  if (!isKeyReaded()) throw new Error('Спершу зчитайте ключ КЕП');
  euSign.SetRuntimeParameter(EU_SIGN_TYPE_PARAMETER, EU_SIGN_TYPE_CADES_BES);
  const data = new TextEncoder().encode(edrpou.trim());
  const p7s: Uint8Array = euSign.SignDataInternal(true, data, false);
  return bytesToBase64(p7s);
}

/**
 * Пароль для OAuth-логіну кабінету ДПС (2026-08-04, автологін ЄРПН):
 * CAdES-підпис рядка ЄДРПОУ З TSP-міткою (у живому cURL логіну підпис має
 * signingTime — тож CAdES-T, на відміну від BES у signApiAuthHeader).
 */
export async function signOAuthPassword(edrpou: string): Promise<string> {
  if (!isKeyReaded()) throw new Error('Спершу зчитайте ключ КЕП');
  await prepareDpsSignMode(_sessionOwner?.issuerCN ?? '');
  const data = new TextEncoder().encode(edrpou.trim());
  const p7s: Uint8Array = euSign.SignDataInternal(true, data, false);
  return bytesToBase64(p7s);
}

/**
 * Кандидати makerId для username OAuth-логіну кабінету
 * («ЄДРПОУ-РНОКПП-makerId»). makerId — 13 цифр, схоже на ms-epoch
 * (1639308702086 ≈ 2021-12-12); кабінет формує його «з даних сертифіката»
 * (cb-sign-ui), але ТОЧНЕ джерело невідоме — дорозвідка на живому ключі.
 * Збираємо з info-обʼєктів сертифікатів усі значення, схожі на 13-значний
 * timestamp (Date → ms, число/рядок 13 цифр), у порядку ймовірності.
 * Діагностика: makerIdDiagnostics() — повний дамп get-методів у консоль.
 */
export function makerIdCandidates(): string[] {
  const out: string[] = [];
  const push = (v: string) => { if (!out.includes(v)) out.push(v); };
  const harvest = (obj: any) => {
    if (!obj) return;
    const proto = Object.getPrototypeOf(obj) ?? obj;
    for (const m of Object.getOwnPropertyNames(proto)) {
      if (!m.startsWith('Get')) continue;
      let v: any;
      try { v = obj[m](); } catch { continue; }
      if (v instanceof Date && !Number.isNaN(v.getTime())) {
        push(String(v.getTime()));
      } else if (typeof v === 'number' && String(Math.trunc(v)).length === 13) {
        push(String(Math.trunc(v)));
      } else if (typeof v === 'string' && /^\d{13}$/.test(v.trim())) {
        push(v.trim());
      }
    }
  };
  // Сертифікати власника (підпис першим — ним логіниться кабінет).
  for (let i = 0; i < 8; i++) {
    let info: any;
    try { info = euSign.EnumOwnCertificates(i); } catch { break; }
    if (!info) break;
    harvest(info);
    try { harvest(info.GetInfoEx?.()); } catch { /* нема — ок */ }
  }
  try { harvest(euSign.GetPrivateKeyOwnerInfo()); } catch { /* ок */ }
  return out;
}

/** Дамп УСІХ Get-методів сертифікатів у консоль — для живої дорозвідки makerId. */
export function makerIdDiagnostics(): Record<string, unknown> {
  const dump: Record<string, unknown> = {};
  const harvest = (label: string, obj: any) => {
    if (!obj) return;
    const proto = Object.getPrototypeOf(obj) ?? obj;
    for (const m of Object.getOwnPropertyNames(proto)) {
      if (!m.startsWith('Get')) continue;
      try {
        const v = obj[m]();
        if (v instanceof Date) dump[`${label}.${m}`] = `${v.toISOString()} (ms=${v.getTime()})`;
        else if (['string', 'number', 'boolean'].includes(typeof v)) dump[`${label}.${m}`] = v;
      } catch { /* метод з аргументами — пропускаємо */ }
    }
  };
  for (let i = 0; i < 8; i++) {
    let info: any;
    try { info = euSign.EnumOwnCertificates(i); } catch { break; }
    if (!info) break;
    harvest(`cert${i}`, info);
  }
  try { harvest('owner', euSign.GetPrivateKeyOwnerInfo()); } catch { /* ок */ }
  // eslint-disable-next-line no-console
  console.info('[eusign] makerId diagnostics:', dump);
  return dump;
}

/**
 * Режим підпису для подання у ДПС.
 * xlong=true → CAdES-X Long (наказ №557: повні дані перевірки, OCSP увімкнено);
 * xlong=false → CAdES-T (лише TSP-мітка; kvt_by_id і fallback).
 */
export async function prepareDpsSignMode(
  issuerCN: string, xlong = false, tspHost?: string,
): Promise<void> {
  euSign.SetRuntimeParameter(
    EU_SIGN_TYPE_PARAMETER,
    xlong ? EU_SIGN_TYPE_CADES_X_LONG : EU_SIGN_TYPE_CADES_T,
  );
  const cas = await getCAs();
  const ca = cas.find((c) => c.issuerCNs.some((cn) => cn === issuerCN));
  // TSP видавця ключа (факт: ПриватБанк віддає мітки через наш проксі, 916Б);
  // tspHost — явний інший надавач (ч.1187: запасні мітки, див. tspCandidates).
  const tsp = euSign.CreateTSPSettings();
  tsp.SetGetStamps(true);
  tsp.SetAddress(tspHost || ca?.tspAddress || 'ca.tax.gov.ua');
  tsp.SetPort(tspHost ? '80' : (ca?.tspAddressPort || '80'));
  euSign.SetTSPSettings(tsp);
  const ocsp = euSign.CreateOCSPSettings();
  if (xlong) {
    // X-Long: OCSP-статус сертифіката вбудовується у підпис.
    ocsp.SetUseOCSP(true);
    ocsp.SetBeforeStore(true);
    ocsp.SetAddress(ca?.ocspAccessPointAddress || 'czo.gov.ua/services/ocsp/');
    ocsp.SetPort(ca?.ocspAccessPointPort || '80');
  } else {
    ocsp.SetUseOCSP(false);
  }
  euSign.SetOCSPSettings(ocsp);
}

/** Внутрішній КЕП (дані всередині p7s). CAdES-T якщо викликано prepareDpsSignMode. */
export function signDataInternal(data: Uint8Array): Uint8Array {
  if (!isKeyReaded()) throw new Error('Спершу зчитайте особистий ключ');
  return euSign.SignDataInternal(true, data, false);
}

/**
 * Підпис ЕДО з МІТКОЮ ЧАСУ (CAdES-T, рішення власника 2026-08-14):
 * сервіс зберігає підписані документи роками, а сертифікат живе 1-2 —
 * без TSP-мітки доказовість «підписано ПОКИ сертифікат діяв» тане.
 * TSP видавця ключа (через наш /eusign/proxy). TSP-сервер недоступний →
 * фолбек BES без мітки (timestamped=false — журнал це помітить).
 * Після підпису режим повертається до дефолтного (BES, мітки OFF) —
 * щоб НЕ вплинути на інші потоки (ДПС сам готує свій режим).
 */
export async function signDataInternalCadesT(
  data: Uint8Array,
): Promise<{ signature: Uint8Array; timestamped: boolean }> {
  if (!isKeyReaded()) throw new Error('Спершу зчитайте особистий ключ');
  const issuerCN = _sessionOwner?.issuerCN ?? '';
  try {
    // CAdES-T: TSP видавця ключа → запасні надавачі (ч.1187), потім BES.
    const { signature } = await signCadesTWithFallbacks(issuerCN, data);
    return { signature, timestamped: true };
  } catch {
    // TSP недоступний — чесний фолбек: підпис БЕЗ мітки.
    euSign.SetRuntimeParameter(EU_SIGN_TYPE_PARAMETER, EU_SIGN_TYPE_CADES_BES);
    const tspOff = euSign.CreateTSPSettings();
    tspOff.SetGetStamps(false);
    euSign.SetTSPSettings(tspOff);
    const signature = signDataInternal(data);
    return { signature, timestamped: false };
  } finally {
    // Відновити дефолти модуля (BES + TSP off + OCSP off).
    euSign.SetRuntimeParameter(EU_SIGN_TYPE_PARAMETER, EU_SIGN_TYPE_CADES_BES);
    const tsp = euSign.CreateTSPSettings();
    tsp.SetGetStamps(false);
    euSign.SetTSPSettings(tsp);
    const ocsp = euSign.CreateOCSPSettings();
    ocsp.SetUseOCSP(false);
    euSign.SetOCSPSettings(ocsp);
  }
}

/**
 * Секція наказу №499: '<SIG>'\\0 + довжина (4 байти LE) + дані.
 * SIG = 'UA1_SIGN' (підписаний блок) або 'UA1_CRYPT' (зашифрований блок).
 */
export function wrapSection(sigName: string, payload: Uint8Array): Uint8Array {
  const sig = new TextEncoder().encode(sigName + '\0');
  const n = payload.length;
  const len = new Uint8Array([n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, (n >>> 24) & 0xff]);
  const out = new Uint8Array(sig.length + 4 + n);
  out.set(sig, 0);
  out.set(len, sig.length);
  out.set(payload, sig.length + 4);
  return out;
}

/** Секція UA1_SIGN (підпис) — як addSignHeaderDp Е-кабінету. */
export function wrapUa1SignSection(p7s: Uint8Array): Uint8Array {
  return wrapSection('UA1_SIGN', p7s);
}

/** Секція UA1_CRYPT (зашифрований блок). */
export function wrapUa1CryptSection(enc: Uint8Array): Uint8Array {
  return wrapSection('UA1_CRYPT', enc);
}

/** Зашифрувати (envelope) на сертифікат отримувача (ДПС), БЕЗ вбудованого підпису. */
/**
 * Зашифрувати на сертифікат(и) отримувача.
 * ВАЖЛИВО: другим отримувачем додаємо СЕБЕ (серт шифрування відправника) —
 * інакше ДПС не має на що зашифрувати квитанцію №2 і НЕ формує її («власник
 * ключа відсутній у списку отримувачів»). Медок робить так само.
 */
export function envelopToCert(certBytes: Uint8Array, data: Uint8Array): Uint8Array {
  if (!isKeyReaded()) throw new Error('Спершу зчитайте особистий ключ');
  const info = euSign.ParseCertificate(certBytes);
  euSign.SaveCertificate(certBytes);
  const issuers = [info.issuer];
  const serials = [info.serial];
  // Додати власний серт шифрування (key agreement) як другого отримувача.
  const own = getOwnAgreementCertificate();
  if (own) {
    const oi = euSign.ParseCertificate(own);
    euSign.SaveCertificate(own);
    issuers.push(oi.issuer);
    serials.push(oi.serial);
  }
  // ФАКТ 2026-08-18 (Медоїд, 4 ітерації діагностики): сертифікат
  // шифрування у сховищі Є і код власника збігається (44159097), але
  // EnvelopDataEx усе одно кладе в originator серійник CA
  // (74B92208…0001) — бо у СЛОТІ лежить ключ ЛИШЕ ДЛЯ ПІДПИСУ (закритого
  // KEP-ключа немає), і бібліотека не може виробити спільний секрет.
  // Тому конверт збираємо ДИНАМІЧНИМ (одноразовим) ключем відправника —
  // штатний шлях ІІТ, KEP-ключ не потрібен, ДПС розшифрує як отримувач.
  // Старий шлях лишається фолбеком (раптом складання динамічним впаде).
  const recipients: Uint8Array[] = own ? [certBytes, own] : [certBytes];
  try {
    const env = euSign.EnvelopDataToRecipientsWithDynamicKey(
      recipients, false, false, data, false);
    console.info('Конверт: ДИНАМІЧНИЙ ключ, отримувачів:', recipients.length);
    return env;
  } catch (e) {
    console.warn('Динамічний конверт не вдався, фолбек EnvelopDataEx:', e);
    return euSign.EnvelopDataEx(issuers, serials, false, data, false);
  }
}

/**
 * Сформувати encryptedId для /kvt_by_id — id пакета у ПОВНОМУ транспортному
 * контейнері наказу №499 (ідентично contentBase64 звіту, який ДПС приймає):
 * CAdES-T підпис id → секція UA1_SIGN → шифрування на серт ДПС → секція
 * UA1_CRYPT → **TRANSPORTABLE-заголовок**. Без заголовка ДПС відповідала
 * NOT_KVT (id не знаходився); голий envelope без секцій — ERROR_DECRYPT.
 * fname — ім'я файлу вихідного звіту (як у транспортному заголовку подання).
 */
export async function encryptReportId(
  reportId: number | string, fname?: string,
): Promise<string> {
  if (!isKeyReaded()) throw new Error('Спершу зчитайте ключ КЕП');
  // Той самий режим підпису, що й у подання звіту (CAdES-T + TSP видавця).
  await prepareDpsSignMode(_sessionOwner?.issuerCN ?? '');
  const idBytes = new TextEncoder().encode(String(reportId));
  const p7s = signDataInternal(idBytes);
  const signSection = wrapUa1SignSection(p7s);
  const cert = await fetchDpsEncryptionCert();
  const encrypted = envelopToCert(cert, signSection);
  const cryptSection = wrapUa1CryptSection(encrypted);
  const container = appendTransportHeader(
    cryptSection, fname ?? `${reportId}.xml`, '',
    getOwnCertificate(),
  );
  return bytesToBase64(container);
}

/**
 * Повний транспортний контейнер для подання у ДПС (наказ №499):
 * CAdES-T підпис → UA1_SIGN → шифрування на серт ДПС → UA1_CRYPT →
 * TRANSPORTABLE (серт ШИФРУВАННЯ власника + email організації).
 * Спільний для панелі документа і Кабінету ДПС («підписати»).
 */
/**
 * Гарантувати, що у АКТИВНОГО ключа є сертифікат ШИФРУВАННЯ (KEP,
 * key agreement) — без нього EnvelopDataEx ставить у originator сміття
 * (факт 2026-08-18, Медоїд/ЦСК «Україна»: контейнер ключа ніс лише серт
 * ПІДПИСУ → originator = серійник ЦСК → ДПС ERROR_DECRYPT без квитанції;
 * у Кроду/ПУМБ обидва серти були — приймалось). Дотягуємо серти по CMP
 * (як у readIntoSlot-fallback, але той спрацьовує лише коли читання
 * ключа ПАДАЄ — тут читання успішне, а KEP-серта нема).
 */
async function ensureOwnAgreementCert(): Promise<void> {
  if (getOwnAgreementCertificate()) return;
  const rec = _activeKeyId ? _keys.find((k) => k.id === _activeKeyId) : null;
  if (!rec) return;
  try {
    const keyInfo = euSign.GetKeyInfoBinary(rec.keyBytes, rec.password);
    const certs = await fetchCertsViaCmp(keyInfo, rec.ca);
    euSign.SaveCertificates(certs);
    euSign.ReadPrivateKeyBinary(rec.keyBytes, rec.password);
    console.info('KEP-сертифікат дотягнуто по CMP:',
      !!getOwnAgreementCertificate());
  } catch (e) {
    console.warn('CMP-дотяжка KEP-сертифіката не вдалася:', e);
  }
  if (!getOwnAgreementCertificate()) {
    // НЕ падаємо: конверт збереться з динамічним ключем (див.
    // envelopToCert). Лише інформуємо у консоль для діагностики.
    console.info('KEP-серта немає — конверт піде з динамічним ключем');
  }
}

/**
 * CO-SIGN: додати ДРУГУ підпис (печатку) у ТОЙ САМИЙ контейнер підпису —
 * два SignerInfo в одному CMS, а НЕ вкладена підпис (факт 2026-08-18,
 * Медоїд: вкладена печатка ламала валідатор ДПС —
 * /validation/validateDp знімав один шар і розбирав внутрішній p7s як
 * XML → HTTP 400 MalformedByteSequence).
 *
 * Слот-API бібліотеки такого не вміє — використовуємо КОНТЕКСТНИЙ
 * (CtxCreate → CtxReadPrivateKeyBinary → CtxAppendSign). Ключ печатки
 * читається у власний контекст, тож активний ключ у слоті (підпис
 * посадової особи) НЕ зачіпається.
 *
 * Повертає p7s з двома підписами; при будь-якій помилці — null
 * (виклик лишає одну підпис, подання не блокується).
 */
const EU_CTX_SIGN_DSTU4145_WITH_GOST34311 = 1;

function appendCoSignature(
  data: Uint8Array, p7s: Uint8Array, key: StoredKey, label: string,
): Uint8Array | null {
  let ctx: any = null;
  let pkCtx: any = null;
  try {
    ctx = euSign.CtxCreate();
    pkCtx = euSign.CtxReadPrivateKeyBinary(ctx, key.keyBytes, key.password);
    const pk = pkCtx?.pkContext ?? pkCtx;
    // Два варіанти виклику: data=null (підпис ВНУТРІШНЯ — дані вже у
    // контейнері signDataInternal) і data=bytes. Успіхом вважаємо ЛИШЕ
    // той, після якого у контейнері СТАЛО 2 підписи.
    // Факт (подача №4, 18.08): co-sign «відпрацював» без винятку, а ДПС
    // побачила у контейнері один-єдиний підпис — тому перевіряємо
    // результат, а не факт повернення.
    const tries: [string, Uint8Array | null][] = [
      ['data=null (внутрішня підпис)', null],
      ['data=bytes', data],
    ];
    for (const [how, payload] of tries) {
      try {
        const both: Uint8Array = euSign.CtxAppendSign(
          pk, EU_CTX_SIGN_DSTU4145_WITH_GOST34311, payload, p7s, true, false);
        const n = countSignatures(both);
        if (n === null || n >= 2) {
          console.info(`CO-SIGN: додано ${label} (${how}); підписів у `
            + `контейнері: ${n ?? '?'}`);
          return both;
        }
        console.warn(`CO-SIGN (${how}): підписів лишилось ${n} — пробуємо далі`);
      } catch (inner) {
        console.warn(`CO-SIGN (${how}) не пройшов:`, inner);
      }
    }
    console.warn(`CO-SIGN (${label}): жоден варіант не дав другої підписи`);
    return null;
  } catch (e) {
    console.warn(`CO-SIGN (${label}) не вдався:`, e);
    return null;
  } finally {
    try { if (pkCtx) euSign.CtxFreePrivateKey(pkCtx); } catch { /* no-op */ }
    try { if (ctx) euSign.CtxFree(ctx); } catch { /* no-op */ }
  }
}

/** Скільки підписів (SignerInfo) у CMS. null — API не відповіло.
 *
 *  Доказ того, що печатка РЕАЛЬНО лягла: у бібліотеці є лише
 *  контекстний `CtxGetSignsCount(context, sign)`, тому створюємо
 *  тимчасовий контекст (ключ не потрібен — рахуємо підписи готового
 *  контейнера). Факт (подача №4, 18.08): co-sign повертав контейнер
 *  БЕЗ другої підписи — ДПС побачила лише один підпис. Мовчазне
 *  «щось повернулось» більше не вважаємо успіхом. */
function countSignatures(p7s: Uint8Array): number | null {
  let ctx: any = null;
  try {
    ctx = euSign.CtxCreate();
    const n = euSign.CtxGetSignsCount(ctx, p7s);
    return typeof n === 'number' ? n : null;
  } catch {
    return null;
  } finally {
    try { if (ctx) euSign.CtxFree(ctx); } catch { /* no-op */ }
  }
}

/**
 * ч.1187 (бій, УАГ Сервіс, ключі ЦСК «Україна»): «Невірний підпис» при
 * підписанні для ДПС. TSP-сервер uakey.com.ua з 14.07.2026 підписує мітки
 * часу новим сертифікатом за ДСТУ 4145 + ДСТУ 7564 («Купина», OID
 * 1.2.804.2.1.1.1.1.3.6.1.1); вбудована бібліотека ІІТ 1.3.1.134 знає лише
 * ГОСТ 34311/SHA (euscpt.js: EU_CTX_HASH_ALGO_*) → мітку перевірити не може
 * (EU_ERROR_BAD_SIGNATURE 0x23), SignDataInternal падає ПІСЛЯ отримання
 * мітки (у журналі проксі — 2 TSP-запити і жодного /sign). Поки бібліотеку
 * не оновлено (TD-EUSIGN-KUPYNA), мітку беремо у надавача, який ще видає
 * ГОСТ-мітки: CAdES-T дозволяє мітку БУДЬ-ЯКОГО кваліфікованого TSP, ДПС
 * перевіряє сертифікат TSP за довірчим списком. Порядок: TSP видавця ключа
 * → ПриватБанк → ПУМБ → ІДД ДПС (без дублів).
 */
export const TSP_FALLBACK_HOSTS: readonly string[] = [
  'acsk.privatbank.ua', 'pki.pumb.ua', 'ca.tax.gov.ua',
];

/** Кандидати TSP для підпису: спершу «свій» (видавця ключа; undefined =
 *  як у prepareDpsSignMode), далі запасні без повторів. */
export function tspCandidates(ownHost: string | null | undefined): (string | undefined)[] {
  const own = (ownHost || '').trim();
  const rest = TSP_FALLBACK_HOSTS.filter((h) => h !== own);
  return [undefined, ...rest];
}

function tspHostOf(ca: EuSignCA | undefined): string {
  return (ca?.tspAddress || 'ca.tax.gov.ua').trim();
}

/** Підпис CAdES-T з перебором надавачів міток часу. Повертає підпис і
 *  хост, чия мітка лягла; кидає ПЕРШУ помилку, якщо не вдався жоден. */
async function signCadesTWithFallbacks(
  issuerCN: string, data: Uint8Array,
): Promise<{ signature: Uint8Array; tspHost: string | undefined }> {
  const cas = await getCAs();
  const ca = cas.find((c) => c.issuerCNs.some((cn) => cn === issuerCN));
  let first: unknown = null;
  for (const host of tspCandidates(tspHostOf(ca))) {
    try {
      await prepareDpsSignMode(issuerCN, false, host);
      const signature = signDataInternal(data);
      if (host) {
        console.warn(`Мітка часу: TSP видавця ключа (${tspHostOf(ca)}) не пройшов `
          + `(${errText(first)}) — використано ${host}`);
      }
      return { signature, tspHost: host };
    } catch (e) {
      first ??= e;
      console.warn(`CAdES-T з TSP ${host ?? tspHostOf(ca)} не вдався:`, e);
    }
  }
  throw new Error(
    `Не вдалося накласти мітку часу на підпис: ${errText(first)}. `
    + `Сервер міток часу видавця ключа (${tspHostOf(ca)}) і запасні `
    + `(${TSP_FALLBACK_HOSTS.join(', ')}) не дали придатної мітки — спробуйте ще раз `
    + 'або зверніться у підтримку.');
}

function errText(e: unknown): string {
  const a = e as { message?: string; description?: string } | null;
  return (a?.message ?? a?.description ?? String(e ?? '')).slice(0, 200);
}

export async function buildSignedContainer(
  bytes: Uint8Array, filename: string, issuerCN: string, email: string,
): Promise<Uint8Array> {
  // ПОРЯДОК ПІДПИСІВ — п.4 розділу III Порядку обміну (наказ Мінфіну
  // №557): ПЕРШИМ підписує ПОСАДОВА ОСОБА, ПЕЧАТКА — ОСТАННЬОЮ.
  // Факт (квитанція №1 Медоїд, 18.08.2026 21:39): коли печатка була
  // першою — «ДОКУМЕНТ НЕ ПРИЙНЯТО … має невірну послідовність підписів
  // КЕП. Підписи документа: - перший - печатка». (Раніше ми прийняли
  // за еталон ВЁРСТКУ бланка квитанції, де рядок «печатка:» просто
  // стоїть згори таблиці — це не порядок підписання.) Тому:
  //   1) ПЕРША підпис у слоті — ПОСАДОВА ОСОБА;
  //   2) ДРУГА (CtxAppendSign, той самий CMS) — ПЕЧАТКА;
  //   3) активним у слоті лишається ключ ОСОБИ (auth-header/квитанції).
  const signerId = _activeKeyId;
  const signer = signerId ? _keys.find((k) => k.id === signerId) : null;
  // Печатка = інший ключ ТІЄЇ САМОЇ особи. Основний ознака — «є ЄДРПОУ,
  // немає РНОКПП» (isSealOwner); запасний — будь-який другий ключ з тим
  // самим кодом (буває печатка без чіткої ознаки у сертифікаті).
  const sameOwner = signer ? _keys.filter((k) =>
    k.id !== signer.id
    && sameKeyCode(keyCode(k.owner), keyCode(signer.owner))) : [];
  const seal = sameOwner.find((k) => isSealOwner(k.owner)) ?? sameOwner[0] ?? null;

  // Наказ №557: CAdES-X Long (повні дані перевірки); OCSP лежить →
  // fallback CAdES-T (шлюз приймає, стара поведінка).
  const signWith = async (ca: string): Promise<Uint8Array> => {
    try {
      await prepareDpsSignMode(ca, true);
      const out = signDataInternal(bytes);
      console.info('Підпис: CAdES-X Long (повні дані перевірки, наказ №557)');
      return out;
    } catch (e) {
      console.warn('CAdES-X Long не вдався (OCSP?), fallback CAdES-T:', e);
      // ч.1187: CAdES-T з перебором TSP (видавець ключа → запасні). Налаштування
      // TSP лишаються на хості, чия мітка лягла, — ту саму мітку візьме й
      // печатка (CtxAppendSign нижче читає глобальні TSP-налаштування).
      return (await signCadesTWithFallbacks(ca, bytes)).signature;
    }
  };

  let p7s: Uint8Array;
  if (seal && signer) {
    // 1) посадова особа — ПЕРША підпис (її ключ активний у слоті).
    await activateKey(signer.id);
    p7s = await signWith(issuerCN);
    // 2) ПЕЧАТКА — ОСТАННЯ, у тому самому CMS (другий SignerInfo).
    // ВИМОГА ВЛАСНИКА (2026-08-18, повторено): якщо юзер зчитав і підпис,
    // і печатку — накладаються ОБИДВІ. Мовчазне подання однією підписом
    // заборонене: краще зупинити подання і показати причину.
    const both = appendCoSignature(bytes, p7s, seal, 'печатку');
    if (!both) {
      throw new Error(
        'Не вдалося накласти ПЕЧАТКУ на документ. Обидві підписи '
        + '(посадова особа + печатка) обов\'язкові — подання зупинено. '
        + 'Перевірте, що ключ печатки зчитано з правильним паролем, '
        + 'і спробуйте підписати ще раз.');
    }
    p7s = both;
    const n = countSignatures(p7s);
    if (n !== null && n < 2) {
      throw new Error(
        `У контейнері виявлено підписів: ${n}, а має бути 2 (посадова `
        + 'особа + печатка). Подання зупинено — підпишіть ще раз.');
    }
    console.info(`Підписи у контейнері: ${n ?? '?'} (особа + печатка)`);
  } else {
    p7s = await signWith(issuerCN);
  }
  const signSection = wrapUa1SignSection(p7s);
  await ensureOwnAgreementCert();  // KEP-серт обов'язковий для envelope
  const cert = await fetchDpsEncryptionCert();
  const encrypted = envelopToCert(cert, signSection);
  const cryptSection = wrapUa1CryptSection(encrypted);
  const ownCert = getOwnAgreementCertificate() ?? getOwnCertificate();
  // Ім'я файлу — як є (…xml нижнім, як пише сама ДПС), БЕЗ upper-case.
  return appendTransportHeader(cryptSection, filename, email, ownCert);
}

/**
 * ДІАГНОСТИКА /kvt_by_id: варіанти формату encryptedId (точний формат ДПС
 * не документує — «encryptedId: String»). Перебираються по черзі, доки ДПС
 * не відповість чимось, окрім NOT_KVT. Після встановлення робочого формату
 * зайві варіанти прибрати, лишити робочий у encryptReportId.
 */
export interface EncIdVariant {
  label: string;
  build: () => Promise<string>;
}

export function encryptReportIdVariants(
  reportId: number | string, fname?: string,
): EncIdVariant[] {
  const idStr = String(reportId);
  const enc = (s: string) => new TextEncoder().encode(s);
  const withMode = async () => {
    await prepareDpsSignMode(_sessionOwner?.issuerCN ?? '');
  };
  const layers = async (payload: Uint8Array) => {
    const p7s = signDataInternal(payload);
    const signSection = wrapUa1SignSection(p7s);
    const cert = await fetchDpsEncryptionCert();
    const encrypted = envelopToCert(cert, signSection);
    return wrapUa1CryptSection(encrypted);
  };
  return [
    {
      label: 'повний контейнер (SIGN+CRYPT+TRANSPORT)',
      build: async () => {
        await withMode();
        const cs = await layers(enc(idStr));
        return bytesToBase64(appendTransportHeader(
          cs, fname ?? `${idStr}.xml`, '', getOwnCertificate()));
      },
    },
    {
      label: 'секції без TRANSPORT (SIGN+CRYPT)',
      build: async () => {
        await withMode();
        return bytesToBase64(await layers(enc(idStr)));
      },
    },
    {
      label: 'підпис+шифр без секцій (env(p7s(id)))',
      build: async () => {
        await withMode();
        const p7s = signDataInternal(enc(idStr));
        const cert = await fetchDpsEncryptionCert();
        return bytesToBase64(envelopToCert(cert, p7s));
      },
    },
    {
      label: 'лише шифрування (env(id))',
      build: async () => {
        const cert = await fetchDpsEncryptionCert();
        return bytesToBase64(envelopToCert(cert, enc(idStr)));
      },
    },
    {
      label: 'повний контейнер, вміст = fname',
      build: async () => {
        await withMode();
        const cs = await layers(enc(fname ?? `${idStr}.xml`));
        return bytesToBase64(appendTransportHeader(
          cs, fname ?? `${idStr}.xml`, '', getOwnCertificate()));
      },
    },
  ];
}

/**
 * Сертифікат шифрування для КАНАЛУ ПОДАННЯ.
 * Ми шлемо через REST API Е-кабінету → шифруємо на ЙОГО сертифікат EK_C_2025
 * («сертифікати API Електронного кабінету для... шифрування» — ДПС).
 * STS_2025_2 — для іншого каналу (SOAP «Єдине вікно»/email), НЕ для API ЕК.
 */
export function fetchDpsEncryptionCert(): Promise<Uint8Array> {
  return fetchBytes(`${ASSETS}/EK_C_2025.cer`);
}

/**
 * Обгорнути підписані+зашифровані дані у транспортний контейнер ДПС
 * (наказ №499): бібліотека ІІТ додає заголовок TRANSPORTABLE сама.
 * caType — CERTYPE АЦСК ('UA1' типово); email — e-mail відправника (може
 * бути порожнім); clientCert — сертифікат підписанта (для CRC/CERTYPE).
 */
export function appendTransportHeader(
  data: Uint8Array, fileName: string, email: string,
  clientCert: Uint8Array, caType = 'UA1',
): Uint8Array {
  return euSign.AppendTransportHeader(caType, fileName, email, clientCert, data);
}

/** Сертифікат поточного зчитаного ключа (для транспортного заголовка). */
export function getOwnCertificate(): Uint8Array {
  // Перший власний сертифікат підписанта (DSTU4145, для підпису)
  const info = euSign.EnumOwnCertificates(0);
  if (!info) throw new Error('Сертифікат ключа не знайдено');
  return euSign.GetCertificate(info.GetIssuer(), info.GetSerial());
}

// keyUsageType сертифіката (бітова маска, euscpt.js).
const KEY_USAGE_DIGITAL_SIGNATURE = 0x0001;
const KEY_USAGE_KEY_AGREEMENT = 0x0010;

export interface OwnCertInfo {
  issuer: string;
  serial: string;
  keyUsage: string;       // людський опис («ЕЦП», «Протоколи розподілу ключів»)
  isSignature: boolean;
  isKeyAgreement: boolean;
}

/** Усі власні сертифікати зчитаного ключа (підпис + шифрування, якщо є). */
export function listOwnCertificates(): OwnCertInfo[] {
  const out: OwnCertInfo[] = [];
  for (let i = 0; i < 8; i++) {
    let info: any;
    try {
      info = euSign.EnumOwnCertificates(i);
    } catch {
      break;
    }
    if (!info) break;
    const kut = Number(info.GetKeyUsageType?.() ?? 0);
    out.push({
      issuer: info.GetIssuer(),
      serial: info.GetSerial(),
      keyUsage: String(info.GetKeyUsage?.() ?? ''),
      isSignature: (kut & KEY_USAGE_DIGITAL_SIGNATURE) !== 0
        || /підпис|signature/i.test(String(info.GetKeyUsage?.() ?? '')),
      isKeyAgreement: (kut & KEY_USAGE_KEY_AGREEMENT) !== 0
        || /розподіл|agreement/i.test(String(info.GetKeyUsage?.() ?? '')),
    });
  }
  return out;
}

/**
 * Сертифікат ШИФРУВАННЯ (key agreement) власника ключа — саме на нього ДПС
 * шифрує квитанцію №2/відповідь. Якщо його нема у ключі — null (тоді ДПС
 * не має на що шифрувати відповідь — ймовірна причина «кв.№2 не приходить»).
 */
export function getOwnAgreementCertificate(): Uint8Array | null {
  // ФАКТ 2026-08-18 (Медоїд): EnumOwnCertificates віддає і сертифікати
  // АЦСК зі сховища — беручи «перший з key agreement», ми клали у
  // конверт серт САМОГО ЦСК → ДПС ERROR_DECRYPT. Тому звіряємо код
  // власника (ЄДРПОУ/РНОКПП) з кодом АКТИВНОГО ключа.
  const wantCode = keyCode(_sessionOwner);
  for (let i = 0; i < 8; i++) {
    let info: any;
    try {
      info = euSign.EnumOwnCertificates(i);
    } catch {
      break;
    }
    if (!info) break;
    const kut = Number(info.GetKeyUsageType?.() ?? 0);
    const usage = String(info.GetKeyUsage?.() ?? '');
    const isAgreement = (kut & KEY_USAGE_KEY_AGREEMENT) !== 0
      || /розподіл|agreement/i.test(usage);
    if (!isAgreement) continue;
    // Код власника беремо з САМОГО сертифіката (ParseCertificateEx) —
    // у info з EnumOwnCertificates методу GetSubjEDRPOUCode може не бути
    // (факт 2026-08-18: код виходив порожній → фільтр не спрацьовував і
    // у конверт ішов серт АЦСК). БЕЗ підтвердженого збігу — НЕ беремо.
    const cert = euSign.GetCertificate(info.GetIssuer(), info.GetSerial());
    let code = '';
    try {
      const ex: any = euSign.ParseCertificateEx
        ? euSign.ParseCertificateEx(cert) : null;
      code = String(ex?.subjEDRPOUCode || ex?.subjDRFOCode || '').trim();
    } catch { /* не вдалось розібрати — код лишається порожній */ }
    console.info('KEP-кандидат:', info.GetIssuer(), '| код:', code || '—',
      '| потрібен:', wantCode || '—');
    if (wantCode && !(code && sameKeyCode(code, wantCode))) continue;
    return cert;
  }
  return null;
}

/**
 * Розібрати квитанцію ДПС до тексту (XML).
 *
 * Квитанція — багатошарова (наказ №499): секції 'UA1_SIGN'\\0+len+p7s
 * (підпис ДПС) та 'UA1_CRYPT'\\0+len+enc (шифр на наш ключ, лише кв.№2).
 * Знімаємо шар за шаром: CRYPT → DevelopData (розшифрувати нашим ключем);
 * SIGN → GetDataFromSignedData (витягти дані з підпису, БЕЗ online-перевірки);
 * доки не дійдемо до XML (<?xml / <DECLAR). Перевірено фактом на кв.№1:
 * UA1_SIGN → CAdES (PKCS#7 SignedData) → XML усередині.
 */
export function developReceipt(receiptBase64: string): string {
  const bytes = base64ToBytes(receiptBase64);
  return bytesToString(unwrapReceipt(bytes, 0));
}

/**
 * ДІАГНОСТИКА: розгорнути НАШ вихідний контейнер (те, що ми шлемо в ДПС)
 * так, як це робить приймальний шлюз — щоб побачити реальний вміст і
 * перевірити підпис. Знімає TRANSPORTABLE-заголовок → UA1_CRYPT (розшифр.
 * нашим ключем) → UA1_SIGN → XML. Повертає XML + інфо про підпис.
 */
export function diagnoseOutgoingContainer(containerBase64: string): {
  xml: string; signInfo: string;
} {
  if (!isKeyReaded()) throw new Error('Спершу зчитайте ключ КЕП');
  let bytes = base64ToBytes(containerBase64);
  // Зняти TRANSPORTABLE\0 + 4 байти довжини (LE) + заголовок.
  const magic = new TextDecoder('ascii').decode(bytes.subarray(0, 13));
  if (magic === 'TRANSPORTABLE') {
    const hlen = bytes[14]! | (bytes[15]! << 8) | (bytes[16]! << 16) | (bytes[17]! << 24);
    bytes = bytes.subarray(18 + hlen);
  }
  // UA1_CRYPT → розшифрувати нашим ключем.
  let sec = parseSection(bytes);
  if (sec?.sig === 'UA1_CRYPT') {
    const dev = euSign.DevelopData(sec.payload);
    bytes = toBytes(dev?.GetData ? dev.GetData() : dev);
  }
  // UA1_SIGN → перевірити підпис (VerifyDataInternal) + витягти дані.
  let signInfo = '(підпис не знайдено)';
  sec = parseSection(bytes);
  const p7s = sec?.sig === 'UA1_SIGN' ? sec.payload : bytes;
  try {
    const vi = euSign.VerifyDataInternal(p7s);
    const s = vi?.GetSignInfo ? vi.GetSignInfo() : vi;
    const t = s?.GetSignType ? s.GetSignType() : '?';
    const who = s?.GetSubjCN ? s.GetSubjCN() : '';
    signInfo = `Підпис ВАЛІДНИЙ. Тип=${t}; підписант=${who}`;
    bytes = toBytes(vi?.GetData ? vi.GetData() : euSign.GetDataFromSignedData(p7s));
  } catch (e: any) {
    signInfo = `Підпис НЕ пройшов перевірку: ${e?.message ?? e}`;
    try { bytes = toBytes(euSign.GetDataFromSignedData(p7s)); } catch { /* — */ }
  }
  return { xml: bytesToString(unwrapReceipt(bytes, 0)), signInfo };
}

export interface ParsedReceipt {
  kind: string;         // «Квитанція №1 (доставка)» / «Квитанція №2 (обробка)»
  accepted: boolean | null;  // прийнято / відхилено / невідомо
  text: string;         // читабельний текст рішення/повідомлення органу
  rawXml: string;       // повний XML (для «показати деталі»)
}

/**
 * Розібрати квитанцію ДПС у читабельний вигляд. Квитанція — XML DECLAR:
 * C_DOC_SUB '991' — доставка (кв.№1), '801'/'802' — обробка (кв.№2).
 * Текст рішення органу — у тегах тіла (шукаємо змістовні текстові вузли).
 */
export function parseReceipt(receiptBase64: string): ParsedReceipt {
  const xml = developReceipt(receiptBase64);
  let doc: Document | null = null;
  try {
    doc = new DOMParser().parseFromString(xml, 'text/xml');
  } catch { /* лишимо як текст */ }

  const sub = doc?.querySelector('DECLARHEAD C_DOC_SUB')?.textContent?.trim() ?? '';
  const isKvt1 = sub === '991';
  const kind = isKvt1 ? 'Квитанція №1 (доставка)' : 'Квитанція №2 (обробка)';

  // Основне рішення органу — тег HRESULT; додатково TEXT/HDOCNAME.
  const pick = (sel: string) =>
    (doc?.querySelector(sel)?.textContent ?? '').trim();
  const result = pick('HRESULT');
  const bodyText = joinReceiptDetails(pick('TEXT'), doc);
  const docName = pick('HDOCNAME');
  const parts = [result, bodyText].filter((t) => t.length >= 3);
  const text = parts.length
    ? parts.join('\n\n')
    : (isKvt1 ? 'Документ доставлено до контролюючого органу.' : (docName || xml));

  const accepted = receiptVerdict(result + ' ' + bodyText, isKvt1);

  return { kind, accepted, text, rawXml: xml };
}

/** Вердикт квитанції з тексту. ч.1185: відмову шукаємо ПЕРШОЮ — «ДОКУМЕНТ НЕ
 *  ПРИЙНЯТО» містить підрядок «прийнято», і кв.№1 з відмовою читалась як
 *  «доставлено» (accepted=null) — у журналі не було ✗. «помилк» — лише коли
 *  іншого вердикту немає: успішна кв.№2 містить «Виявлені помилки: 00». Та сама
 *  логіка, що `dps_receipts.receipt_verdict` на сервері. */
export function receiptVerdict(text: string, isKvt1: boolean): boolean | null {
  const low = text.toLowerCase().replace(/прийнятт[яю]\s*\/\s*неприйнятт[яю]|неприйнятт[яю]\s*\/\s*прийнятт[яю]/g, ' ');
  if (/не\s*прийнято|відмов|відхил/.test(low)) return false;
  if (/прийнято|зареєстров|збережено|виконано вдало/.test(low)) return isKvt1 ? null : true;
  if (/помилк/.test(low)) return false;
  return null;
}

/** ч.1185: причини відмови ДПС кладе НЕ в HRESULT, а рядками T1RXXXXG1S
 *  (бій УАГ Сервіс: «ERROR_XSD» без пояснення, хоча кв.№1 містила «C_DOC_VER …
 *  обмеженню на значення '9'» і «… має відповідати '{HNY}'»). Збираємо їх у
 *  текст квитанції, щоб причина була видна у журналі і на бланку. */
export function joinReceiptDetails(text: string, doc: Document | null): string {
  const rows = Array.from(doc?.querySelectorAll('T1RXXXXG1S') ?? [])
    .map((n) => (n.textContent ?? '').replace(/\s+/g, ' ').trim())
    .filter((t) => t.length >= 3);
  return [text, ...rows].filter((t) => t.length >= 3).join('\n');
}

/** Структуровані поля квитанції для друкованого бланка (форма J1499202). Усе
 *  береться з тегів XML квитанції — той самий вміст, що показує Е-кабінет. */
export interface ReceiptFields {
  num: 1 | 2;
  tin: string;          // ЄДРПОУ/РНОКПП платника (HTIN)
  name: string;         // найменування платника (HNAME)
  email: string;        // HEMAIL
  docKod: string;       // код форми документа (HDOCKOD)
  docName: string;      // назва документа звітності (HDOCNAME)
  fileName: string;     // ім'я файлу (HFILENAME)
  docStan: string;      // стан документа: Звітна/Уточнююча (HDOCSTAN)
  period: string;       // назва звітного періоду (HPERIOD)
  year: string;         // звітний рік (HZY)
  term: string;         // граничний термін подання (HDTERM)
  bosCode: string;      // РНОКПП керівника/підписанта (HKBOS)
  bos: string;          // ПІБ керівника/підписанта (HBOS)
  buhCode: string;      // РНОКПП бухгалтера (HKBUH)
  buh: string;          // ПІБ бухгалтера (HBUH)
  orgCode: string;      // код контролюючого органу (HKODSTI)
  stiName: string;      // назва контролюючого органу (HSTI)
  result: string;       // HRESULT — результат обробки
  date: string;         // HDATE
  time: string;         // HTIME
  regNum: string;       // реєстраційний № (HNUMREG)
  sender: string;       // відправник (HSENDER)
  text: string;         // TEXT — розгорнутий текст рішення/попереджень
  accepted: boolean | null;
}

function fmtDgDate(v: string): string {
  // DGDate у квитанції — 8 цифр ДДММРРРР → ДД.ММ.РРРР
  const m = v.match(/^(\d{2})(\d{2})(\d{4})$/);
  return m ? `${m[1]}.${m[2]}.${m[3]}` : v;
}

/** Розшифрувати квитанцію ключем і повернути (поля + сирий XML). XML можна
 *  закешувати на сервері, щоб далі будувати бланк БЕЗ ключа. */
export function developReceiptToXml(receiptBase64: string): string {
  return developReceipt(receiptBase64);
}

export function parseReceiptFields(receiptBase64: string): ReceiptFields {
  return parseReceiptFieldsFromXml(developReceipt(receiptBase64));
}

/** Ті самі поля, але з уже РОЗШИФРОВАНОГО XML (кеш receiptN_xml) — без ключа. */
export function parseReceiptFieldsFromXml(xml: string): ReceiptFields {
  let doc: Document | null = null;
  try { doc = new DOMParser().parseFromString(xml, 'text/xml'); } catch { /* */ }
  const g = (sel: string) => (doc?.querySelector(sel)?.textContent ?? '').trim();

  const sub = g('DECLARHEAD C_DOC_SUB');
  const num: 1 | 2 = sub === '991' ? 1 : 2;
  const result = g('HRESULT');
  const bodyText = joinReceiptDetails(g('TEXT'), doc);
  const accepted = receiptVerdict(result + ' ' + bodyText, num === 1);

  return {
    num,
    tin: g('HTIN'),
    name: g('HNAME'),
    email: g('HEMAIL'),
    docKod: g('HDOCKOD'),
    docName: g('HDOCNAME'),
    fileName: g('HFILENAME'),
    docStan: g('HDOCSTAN'),
    period: g('HPERIOD'),
    year: g('HZY'),
    term: fmtDgDate(g('HDTERM')),
    bosCode: g('HKBOS'),
    bos: g('HBOS'),
    buhCode: g('HKBUH'),
    buh: g('HBUH'),
    orgCode: g('HKODSTI'),
    stiName: g('HSTI'),
    result,
    date: fmtDgDate(g('HDATE')),
    time: g('HTIME'),
    regNum: g('HNUMREG'),
    sender: g('HSENDER'),
    text: bodyText,
    accepted,
  };
}

function unwrapReceipt(data: Uint8Array, depth: number): Uint8Array {
  if (depth > 6) throw new Error('Занадто багато шарів квитанції');
  // Дійшли до XML — це вміст.
  if (looksLikeXml(data)) return data;

  const sec = parseSection(data);
  if (sec) {
    if (sec.sig === 'UA1_CRYPT') {
      if (!isKeyReaded()) throw new Error('Спершу зчитайте ключ КЕП');
      const dev = euSign.DevelopData(sec.payload);
      const out = dev?.GetData ? dev.GetData() : dev;
      return unwrapReceipt(toBytes(out), depth + 1);
    }
    if (sec.sig === 'UA1_SIGN') {
      const inner = euSign.GetDataFromSignedData(sec.payload);
      return unwrapReceipt(toBytes(inner), depth + 1);
    }
    // Невідома секція — спробувати payload далі.
    return unwrapReceipt(sec.payload, depth + 1);
  }

  // Не секція: можливо чистий CMS (SignedData/EnvelopedData) без обгортки.
  try {
    const inner = euSign.GetDataFromSignedData(data);
    return unwrapReceipt(toBytes(inner), depth + 1);
  } catch { /* не підпис */ }
  try {
    if (isKeyReaded()) {
      const dev = euSign.DevelopData(data);
      const out = dev?.GetData ? dev.GetData() : dev;
      return unwrapReceipt(toBytes(out), depth + 1);
    }
  } catch { /* не шифр */ }
  return data;  // віддаємо як є (краще показати сирі байти, ніж впасти)
}

/** Секція наказу №499: '<SIG>'\\0 + 4 байти довжини (LE) + payload. */
function parseSection(data: Uint8Array): { sig: string; payload: Uint8Array } | null {
  const z = data.indexOf(0);
  if (z <= 0 || z > 16) return null;
  const sig = new TextDecoder('ascii').decode(data.subarray(0, z));
  if (sig !== 'UA1_SIGN' && sig !== 'UA1_CRYPT') return null;
  if (data.length < z + 5) return null;
  const len = data[z + 1]! | (data[z + 2]! << 8) | (data[z + 3]! << 16) | (data[z + 4]! << 24);
  const start = z + 5;
  return { sig, payload: data.subarray(start, start + len) };
}

function looksLikeXml(data: Uint8Array): boolean {
  // Пропустити можливий BOM/пробіли, шукати '<'
  for (let i = 0; i < Math.min(data.length, 8); i++) {
    const c = data[i]!;
    if (c === 0x3c) return true;  // '<'
    if (c !== 0xef && c !== 0xbb && c !== 0xbf && c !== 0x20 && c !== 0x0a && c !== 0x0d) break;
  }
  return false;
}

function toBytes(v: any): Uint8Array {
  if (v instanceof Uint8Array) return v;
  if (Array.isArray(v)) return new Uint8Array(v);
  if (typeof v === 'string') return base64ToBytes(v);
  if (v?.buffer) return new Uint8Array(v.buffer);
  return new Uint8Array(0);
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr;
}

export function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}

function bytesToString(bytes: Uint8Array): string {
  try {
    return new TextDecoder('windows-1251' as any).decode(bytes);
  } catch {
    return new TextDecoder().decode(bytes);
  }
}

function safeCall<T>(fn: () => T): T | undefined {
  try { return fn(); } catch { return undefined; }
}

function normalizeErr(e: any): Error {
  const msg = typeof e === 'string' ? e
    : e?.message ?? (e?.GetMessage ? e.GetMessage() : String(e));
  return new Error(msg);
}

// ── ч.1357: Держстат (приймальний шлюз zvitdirect.stat.gov.ua) ─────────────────────
// Той самий «електронний конверт» (додаток 1 до Порядку № 3 Держстату = формат
// наказу № 499 ДПС): CAdES-підпис → UA1_SIGN → шифрування на сертифікат ШЛЮЗУ
// ДЕРЖСТАТУ → UA1_CRYPT → TRANSPORTABLE. Відмінність від ДПС: у транспортному
// заголовку обовʼязковий тег PRG_TYPE (назва ПЗ, ≤10 символів) — його пише
// AppendTransportHeaderEx бібліотеки ІІТ.

/** Назва ПЗ для PRG_TYPE транспортного заголовка Держстату (≤10 символів). */
export const STAT_PRG_TYPE = 'OBLIKPLUS';
export const STAT_PRG_VER = '1.0';

/** Сертифікат ШИФРУВАННЯ приймального шлюзу Держстату (COEZ_25, Key Agreement). */
export function fetchStatEncryptionCert(): Promise<Uint8Array> {
  return fetchBytes(`${ASSETS}/COEZ_25_crypt.cer`);
}

/**
 * Транспортний заголовок із назвою/версією ПЗ (теги PRG_TYPE / PRG_VER) —
 * вимога формату конверта Держстату.
 */
export function appendTransportHeaderEx(
  data: Uint8Array, fileName: string, email: string, clientCert: Uint8Array,
  programName = STAT_PRG_TYPE, programVersion = STAT_PRG_VER, caType = 'UA1',
): Uint8Array {
  return euSign.AppendTransportHeaderEx(
    caType, programName, programVersion, null, fileName, email, clientCert, data);
}

/**
 * Повний конверт для шлюзу Держстату: підпис XML зчитаним ключем → UA1_SIGN →
 * шифрування на сертифікат шлюзу (+ власний серт шифрування другим отримувачем,
 * щоб квитанції можна було розшифрувати) → UA1_CRYPT → TRANSPORTABLE з PRG_TYPE.
 */
export async function buildStatContainer(
  xml: Uint8Array, fileName: string, email: string, issuerCN: string,
): Promise<Uint8Array> {
  if (!isKeyReaded()) throw new Error('Спершу зчитайте ключ КЕП');
  await prepareDpsSignMode(issuerCN);
  const p7s = signDataInternal(xml);
  const signSection = wrapUa1SignSection(p7s);
  await ensureOwnAgreementCert();
  const cert = await fetchStatEncryptionCert();
  const encrypted = envelopToCert(cert, signSection);
  const cryptSection = wrapUa1CryptSection(encrypted);
  const ownCert = getOwnAgreementCertificate() ?? getOwnCertificate();
  return appendTransportHeaderEx(cryptSection, fileName, email, ownCert);
}

/**
 * Підписаний аргумент запиту до шлюзу (signedEDRPOU / signedEmail / signedMsgId):
 * «блок XXX_SIGN» = секція UA1_SIGN із внутрішнім підписом тексту (CAdES-BES).
 */
export function signStatArgument(text: string): string {
  if (!isKeyReaded()) throw new Error('Спершу зчитайте ключ КЕП');
  euSign.SetRuntimeParameter(EU_SIGN_TYPE_PARAMETER, EU_SIGN_TYPE_CADES_BES);
  const p7s: Uint8Array = euSign.SignDataInternal(true, new TextEncoder().encode(text), false);
  return bytesToBase64(wrapUa1SignSection(p7s));
}

/**
 * Квитанція шлюзу Держстату → XML: зняти TRANSPORTABLE-заголовок (якщо є) і далі
 * шар за шаром, як квитанції ДПС (UA1_CRYPT розшифрувати нашим ключем, UA1_SIGN
 * зняти підпис Держстату).
 */
export function developStatReceiptToXml(bodyBase64: string): string {
  let bytes = base64ToBytes(bodyBase64);
  const magic = new TextDecoder('ascii').decode(bytes.subarray(0, 13));
  if (magic === 'TRANSPORTABLE') {
    const hlen = bytes[14]! | (bytes[15]! << 8) | (bytes[16]! << 16) | (bytes[17]! << 24);
    bytes = bytes.subarray(18 + hlen);
  }
  return bytesToString(unwrapReceipt(bytes, 0));
}
